import React, { useCallback, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { Upload, X, Star, Play, GripVertical, ImagePlus, Video, FileText } from "lucide-react";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";

/**
 * Compress an image client-side using canvas.
 * Returns a File. Falls back to original on error / already-small.
 */
async function compressImage(file, { maxDim = 1920, quality = 0.82 } = {}) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const kindOf = (file) => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "document";
};

/**
 * Reusable media manager.
 *
 * props:
 *  - value: string[] (URLs — for images/videos)
 *  - onChange(newValue)
 *  - accept: "image" | "video" | "document"
 *  - isPrivate: bool (upload as private, admin only)
 *  - label: string
 *  - maxItems: number
 *  - showCoverBadge: bool (for images, badge "Couverture" on first)
 */
export default function MediaUploader({
  value = [],
  onChange,
  accept = "image",
  isPrivate = false,
  label,
  maxItems = 24,
  showCoverBadge = true,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const acceptAttr = {
    image: "image/jpeg,image/png,image/webp,image/gif",
    video: "video/mp4,video/quicktime,video/webm,video/x-matroska",
    document: "application/pdf,image/jpeg,image/png,image/webp,.doc,.docx,.xls,.xlsx",
  }[accept];

  const upload = useCallback(
    async (files) => {
      const list = Array.from(files || []).slice(0, Math.max(0, maxItems - value.length));
      if (!list.length) return;
      setUploading(list.length);
      const uploaded = [];
      for (const raw of list) {
        try {
          const file =
            accept === "image" ? await compressImage(raw, { maxDim: 1920, quality: 0.82 }) : raw;
          const form = new FormData();
          form.append("file", file);
          const params = new URLSearchParams();
          params.set("kind", kindOf(file));
          if (isPrivate) params.set("is_private", "true");
          // Let the browser/Axios add the multipart boundary automatically.
          // Manually setting Content-Type can make FastAPI reject the upload.
          const r = await api.post(`/media/upload?${params.toString()}`, form);
          uploaded.push(
            accept === "document"
              ? { name: raw.name, url: r.data.url, id: r.data.id, size: r.data.size, content_type: r.data.content_type }
              : r.data.url
          );
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'upload vers le stockage");
        } finally {
          setUploading((n) => Math.max(0, n - 1));
        }
      }
      if (uploaded.length) onChange([...(value || []), ...uploaded]);
      if (inputRef.current) inputRef.current.value = "";
    },
    [accept, isPrivate, maxItems, onChange, value]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files);
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const setCover = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    const [it] = next.splice(idx, 1);
    next.unshift(it);
    onChange(next);
  };
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const next = [...value];
    const [it] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, it);
    onChange(next);
  };

  const AcceptIcon = accept === "video" ? Video : accept === "document" ? FileText : ImagePlus;

  const previewSrc = (item) => resolveMediaUrl(item);

  return (
    <div className="space-y-3" data-testid={`media-uploader-${accept}`}>
      {label && (
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-semibold text-brand-navy">{label}</label>
          <span className="text-xs text-slate-500">
            {value.length}/{maxItems}
          </span>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
          dragOver
            ? "border-brand-orange bg-brand-orange/5"
            : "border-slate-300 bg-slate-50/60 hover:border-brand-orange/60 hover:bg-brand-orange/5"
        }`}
        data-testid={`dropzone-${accept}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple
          hidden
          onChange={(e) => upload(e.target.files)}
        />
        <AcceptIcon className="mx-auto h-8 w-8 text-brand-orange" />
        <div className="mt-2 text-sm font-semibold text-brand-navy">
          {accept === "image" && "Glissez vos photos ou cliquez pour parcourir"}
          {accept === "video" && "Ajoutez une vidéo (MP4, MOV, WebM)"}
          {accept === "document" && "Ajoutez un document (PDF, image, Word, Excel)"}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {accept === "image" && "JPG, PNG, WebP — compression auto, max 15 Mo par image"}
          {accept === "video" && "Jusqu'à 200 Mo par vidéo"}
          {accept === "document" && "Jusqu'à 30 Mo par fichier"}
          {isPrivate && " • Fichier privé — visible uniquement dans l'Espace Owner"}
        </div>
        {uploading > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-brand-orange">
            <Upload className="h-3.5 w-3.5 animate-pulse" />
            Envoi en cours ({uploading})…
          </div>
        )}
      </div>

      {accept !== "document" && value.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="media" direction="horizontal">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps} className="flex flex-wrap gap-3">
                {value.map((item, idx) => (
                  <Draggable key={`${item}-${idx}`} draggableId={`${item}-${idx}`} index={idx}>
                    {(p) => (
                      <div
                        ref={p.innerRef}
                        {...p.draggableProps}
                        className="relative group w-28 h-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shadow-sm"
                        data-testid={`media-item-${idx}`}
                      >
                        {accept === "image" ? (
                          <img src={previewSrc(item)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-brand-navy text-white">
                            <Play className="h-8 w-8" />
                          </div>
                        )}
                        {showCoverBadge && accept === "image" && idx === 0 && (
                          <div className="absolute top-1 left-1 bg-brand-orange text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded">
                            Couverture
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                          <button
                            type="button"
                            {...p.dragHandleProps}
                            className="p-1 rounded bg-white/90 text-brand-navy"
                            title="Glisser pour réordonner"
                          >
                            <GripVertical className="h-3.5 w-3.5" />
                          </button>
                          <div className="flex gap-1">
                            {accept === "image" && idx !== 0 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCover(idx);
                                }}
                                className="p-1 rounded bg-brand-orange text-white"
                                title="Définir comme couverture"
                                data-testid={`set-cover-${idx}`}
                              >
                                <Star className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(idx);
                              }}
                              className="p-1 rounded bg-red-500 text-white"
                              title="Supprimer"
                              data-testid={`remove-media-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {accept === "document" && value.length > 0 && (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
          {value.map((d, i) => (
            <li key={i} className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-brand-orange shrink-0" />
                <a
                  href={previewSrc(d)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-brand-navy truncate hover:underline"
                >
                  {d.name || d.url}
                </a>
                {isPrivate && (
                  <span className="text-[9px] font-bold uppercase tracking-widest text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                    Privé
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-slate-400 hover:text-red-500"
                data-testid={`remove-doc-${i}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
